import { useCallback, useRef, useState, type DragEvent } from "react";
import { GripVertical, LayoutTemplate, Plus } from "lucide-react";
import type { EditorState } from "./useEditorState";
import { SECTION_ICONS, SECTION_LABELS } from "./constants";

/** Strip HTML tags from a string (used to clean rich-text prop values). */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}

function getFirstHeading(props: Record<string, unknown>): string | null {
  // Check common heading fields
  for (const key of ["heading", "title", "question"]) {
    const val = props[key];
    if (typeof val === "string" && val.trim()) {
      const clean = stripHtml(val).trim();
      return clean || null;
    }
  }
  return null;
}

interface OutlineRailProps {
  editor: EditorState;
  onScrollToSection: (sectionId: string) => void;
  onOpenLibrary: () => void;
}

export function OutlineRail({ editor, onScrollToSection, onOpenLibrary }: OutlineRailProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<"above" | "below">("below");
  const dragCounter = useRef(0);

  const handleDragStart = useCallback(
    (e: DragEvent<HTMLButtonElement>, index: number) => {
      setDragIndex(index);
      e.dataTransfer.effectAllowed = "move";
      // Required for Firefox
      e.dataTransfer.setData("text/plain", String(index));
    },
    [],
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLButtonElement>, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      // Determine if cursor is in top or bottom half of the element
      const rect = e.currentTarget.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      setHoverPosition(e.clientY < midY ? "above" : "below");
      setHoverIndex(index);
    },
    [],
  );

  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLButtonElement>, index: number) => {
      e.preventDefault();
      dragCounter.current++;
      setHoverIndex(index);
    },
    [],
  );

  const handleDragLeave = useCallback(() => {
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      setHoverIndex(null);
      dragCounter.current = 0;
    }
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLButtonElement>, dropIndex: number) => {
      e.preventDefault();
      dragCounter.current = 0;
      if (dragIndex === null || dragIndex === dropIndex) {
        setDragIndex(null);
        setHoverIndex(null);
        return;
      }
      // Calculate target index based on hover position
      let toIndex = dropIndex;
      if (hoverPosition === "below" && dropIndex < dragIndex) {
        toIndex = dropIndex + 1;
      } else if (hoverPosition === "above" && dropIndex > dragIndex) {
        toIndex = dropIndex - 1;
      }
      // Clamp
      toIndex = Math.max(0, Math.min(editor.content.length - 1, toIndex));
      editor.reorderSection(dragIndex, toIndex);
      setDragIndex(null);
      setHoverIndex(null);
    },
    [dragIndex, hoverPosition, editor],
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setHoverIndex(null);
    dragCounter.current = 0;
  }, []);

  return (
    <nav className="w-[190px] flex-none bg-paper hair-r overflow-y-auto py-3.5 px-2.5">
      <h3 className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-ink-5 px-2 pb-2.5">
        Page sections
      </h3>

      {editor.content.map((row, index) => {
        const isSelected = editor.selectedSectionId === row.id;
        const isHidden = row.enabled === false;
        const Icon = SECTION_ICONS[row.type] || LayoutTemplate;
        const label = SECTION_LABELS[row.type] || row.type.toUpperCase();
        const heading = getFirstHeading(row.props);
        const isDragging = dragIndex === index;
        const isHovered = hoverIndex === index && dragIndex !== null && dragIndex !== index;

        return (
          <button
            key={row.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnter={(e) => handleDragEnter(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            onClick={() => {
              editor.selectSection(row.id ?? null);
              if (row.id) onScrollToSection(row.id);
            }}
            className={`relative flex items-center gap-1 w-full px-1 py-2 rounded-[9px] border-0 bg-transparent text-left text-[12.5px] transition-colors group ${
              isSelected
                ? "bg-tide-ss text-tide-d"
                : "text-ink-3 hover:bg-paper-2"
            } ${isHidden ? "opacity-45" : ""} ${isDragging ? "opacity-40" : ""}`}
          >
            {/* Drop indicator line */}
            {isHovered && hoverPosition === "above" && (
              <span className="absolute -top-[1px] left-2 right-2 h-[2px] bg-tide rounded-full" />
            )}
            {isHovered && hoverPosition === "below" && (
              <span className="absolute -bottom-[1px] left-2 right-2 h-[2px] bg-tide rounded-full" />
            )}

            {/* Drag handle */}
            <span className="flex-none w-[16px] grid place-items-center text-ink-5 opacity-0 group-hover:opacity-60 cursor-grab active:cursor-grabbing transition-opacity">
              <GripVertical size={11} strokeWidth={2} />
            </span>

            <span
              className={`w-[26px] h-[26px] flex-none rounded-[7px] grid place-items-center ${
                isSelected
                  ? "bg-tide-s text-tide"
                  : "bg-canvas-2 text-ink-4"
              }`}
            >
              <Icon size={12} strokeWidth={1.8} />
            </span>
            <span className="min-w-0 flex-1">
              <span
                className={`block text-[9.5px] font-bold tracking-[0.06em] uppercase ${
                  isSelected ? "text-tide" : "text-ink-5"
                }`}
              >
                {label}
              </span>
              {heading && (
                <span
                  className={`block font-medium truncate max-w-[98px] ${
                    isHidden ? "line-through" : ""
                  }`}
                >
                  {heading}
                </span>
              )}
            </span>
          </button>
        );
      })}

      <button
        onClick={onOpenLibrary}
        className="mt-2 w-full border-[1.5px] border-dashed border-ink-6 bg-transparent rounded-[9px] py-2 text-[12px] font-semibold text-ink-4 hover:border-tide hover:text-tide hover:bg-tide-ss transition-colors cursor-pointer"
      >
        <Plus size={12} strokeWidth={2.5} className="inline mr-1" />
        Add section
      </button>
    </nav>
  );
}
